import React, { useState, useEffect, useMemo } from "react";
import { StyleSheet } from "react-native";
import { TextInput, useTheme, Paragraph, Button } from "react-native-paper";
import { useDispatch, useSelector } from "react-redux";

import { useTranslation } from "react-i18next";
import { RegisterOptions, useForm } from "react-hook-form";
import { ACCOUNT, EBINQUIRY } from "app/shared/services/services-model.type";
import { useDesktopMediaQuery } from "app/shared/util/media-query";
import {
  getTransferLocalAccountByPhoneSelector,
  getTransferLocalMakeFeeSelector,
  getTransferLocalResAccountDetailSelector,
  getTransferLocalStoreSelector,
} from "../../reducer/selectors";
import {
  nonAccentVietnamese,
  regexCharacterMarkSpaceDot,
  regexNumber,
  replaceEnterKey,
  replaceSpace,
  ValidatePhoneField,
} from "app/shared/util/validate";
import { DataTransferCreate } from "app/modules/transfer/reducer/types";
import { Constant } from "app/shared/constant/constant";
import { amountConfig, findAccountSameCif } from "app/modules/transfer/transfer-utils/transfer.utils";
import {
  fetchTransferLocalAccountDetailRequest,
  fetchTransferLocalAccountDetailSuccess,
  fetchTransferLocalDetailByPhoneRequest,
  setTransferLocalStore,
} from "../../reducer/actions";
import { PROFILE_BENE } from "app/modules/beneficiaries/utils/bene.utils";
import { spacing } from "app/shared/styles/spacing";
import { Block } from "app/shared/components/blocks/block";
import IconsYellowBanking from "app/shared/components/icons/icon-yellow-banking/IconsYellowBanking";
import { InputText, InputSegment, InputTextCurrency, InputTextMultiline } from "app/shared/components/form/input";
import SelectAccountsBeneModal from "app/shared/components/modal/bene-account-source/select-account-bene.modal";
import { StackSpacing, QueueSpacing } from "@components/spacing";
import { FeeDetailModalNew } from "app/shared/components/modal/make-fee/fee-detail.modal";
import FormViewFee from "app/shared/components/form/fee/form-fee";
import { resetListBene } from "app/modules/beneficiaries/reducer/actions";
import { testProps } from "app/shared/util/test-props";
import { getLimitRemainSelector } from "app/modules/account-management/reducer/selectors";

export interface FormTransLocalProps {
  accountDebit: ACCOUNT;
  onSubmit: Function;
  onCancel: Function;
  requestMakeFee: Function;
  requestCreate: Function;
  showModal: Function;
  closeModal: Function;
}

enum FormEnum {
  customer,
  accountNo,
  accountName,
  amount,
  description,
  feeType,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export interface TransLocalData {
  customer: string;
  accountNo: string;
  accountName: string;
  amount: string;
  description: string;
  feeType: string;
}

const TypeCustomer = {
  OTHER: "OTHER",
  PHONE: "PHONE",
  ACCOUNT_NO: "ACCOUNT_NO",
};

const FormTransLocal = (props: FormTransLocalProps) => {
  const { accountDebit, onSubmit, requestMakeFee, requestCreate, onCancel, showModal, closeModal } = props;
  const { colors } = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isDesktop = useDesktopMediaQuery();

  const transferLocalStore = useSelector(getTransferLocalStoreSelector);
  const limitRemainStore = useSelector(getLimitRemainSelector);
  const responseMakeFee = useSelector(getTransferLocalMakeFeeSelector);
  const accountCredit = useSelector(getTransferLocalResAccountDetailSelector);
  const accountCreditByPhone = useSelector(getTransferLocalAccountByPhoneSelector);

  const [validateAmount, setValidateAmount] = useState({ maxAmount: 0, errorMaxDebit: "" });
  const [dataFee, setDataFee] = useState<EBINQUIRY | undefined>(undefined);
  const [isBene, setIsBene] = useState<boolean>(false);
  const [accountBene, setAccountBene] = useState<PROFILE_BENE | undefined>();
  const [customerType, setCustomerType] = React.useState<string>(TypeCustomer.OTHER);
  const [searchCustomer, setSearchCustomer] = React.useState<boolean>(false);
  const [accountDetail, setAccountDetail] = React.useState<ACCOUNT | undefined>();

  const { control, handleSubmit, setValue, getValues, clearErrors, trigger } = useForm<TransLocalData>({
    defaultValues: {
      customer: "",
      accountNo: "",
      accountName: "",
      amount: "",
      description: "",
      feeType: "0",
    },
    mode: "onChange",
  });

  const validateSchemeForm: ValidDataSchemeFormData = {
    customer: {
      required: {
        value: true,
        message: t("TRANFER_THONG_BAO_NHAP_THIEU_TAI_KHOAN"),
      },
    },
    accountNo: {
      required: {
        value: true,
        message: t("TRANFER_THONG_BAO_NHAP_THIEU_TAI_KHOAN"),
      },
      validate: {
        duplicate: (value) => {
          if (accountDebit?.ACCOUNTNO?.length && value === accountDebit.ACCOUNTNO) {
            return t("TRANFER_THONG_BAO_TRUNG_TK_NGUON").toString();
          }
          return true;
        },
        regex: (value: string) => {
          const regex = regexNumber();
          if (!regex.test(value)) {
            return t("TRANFER_LOI_TAI_KHOAN").toString();
          }
          return true;
        },
      },
    },
    accountName: {
      required: {
        value: false,
        message: "",
      },
    },
    amount: {
      required: {
        value: true,
        message: t("TRANFER_THONG_BAO_NHAP_THIEU_SO_TIEN"),
      },
      validate: {
        amountMin: (value) => {
          if (Number(value) < 10000) {
            return t("COMMON_SO_TIEN_KHONG_DUOC_DUOI_10000D").toString();
          }
          return true;
        },
        amountMax: (value) => {
          if (validateAmount?.maxAmount && Number(value) > validateAmount?.maxAmount) {
            return validateAmount?.errorMaxDebit;
          }
          return true;
        },
      },
    },
    description: {
      required: {
        value: true,
        message: t("TRANFER_THONG_BAO_CHUA_NHAP_NOI_DUNG"),
      },
      validate: {
        regex: (value) => {
          const regex = regexCharacterMarkSpaceDot();
          if (!regex.test(value)) {
            return t("TRANFER_NOI_DUNG_CHUYEN_TIEN_KHONG_HOP_LE").toString();
          }
          return true;
        },
      },
    },
    feeType: {
      required: {
        value: false,
        message: "",
      },
    },
  };

  const handleRequestCreate = (value: TransLocalData): void => {
    const dataUpdate: DataTransferCreate = {
      ACCOUNT_CREDIT: {
        ACCOUNTNO: value.accountNo,
        ACCOUNTTYPE: "10",
        ACCOUNTNAME: value.accountName,
      },
      PHONE: customerType === TypeCustomer.PHONE ? value.customer : "",
      AMOUNT: value.amount,
      DESCRIPTION: value.description,
      FEE_TYPE: value.feeType === "0" ? Constant.TRANSFER.TRANSFER_FEE_TYPE.O : Constant.TRANSFER.TRANSFER_FEE_TYPE.I,
      BENE: isBene && accountBene?.ACCOUNT_NUMBER?.length && accountBene?.ACCOUNT_NUMBER === value.accountNo ? accountBene : undefined,
    };
    requestCreate(dataUpdate);
  };

  const handlerCheckAccountCredit = (value?: string) => {
    if (!value?.length) {
      setCustomerType(TypeCustomer.OTHER);
      if (accountCredit?.ACCOUNTNO?.length) dispatch(setTransferLocalStore({ ...transferLocalStore, accountCredit: undefined }));
      return;
    }
    setSearchCustomer(false);
    const accountSameCif = findAccountSameCif(value);
    if (accountSameCif) {
      if (customerType !== TypeCustomer.ACCOUNT_NO) setCustomerType(TypeCustomer.ACCOUNT_NO);
      dispatch(fetchTransferLocalAccountDetailSuccess(accountSameCif));
    } else {
      if (ValidatePhoneField(value)) {
        if (customerType !== TypeCustomer.PHONE) setCustomerType(TypeCustomer.PHONE);
        dispatch(fetchTransferLocalDetailByPhoneRequest(value));
      } else {
        if (customerType !== TypeCustomer.ACCOUNT_NO) setCustomerType(TypeCustomer.ACCOUNT_NO);
        dispatch(fetchTransferLocalAccountDetailRequest({ ACCOUNTNO: value, ACCOUNTTYPE: "10" }, true));
      }
    }
  };

  const handlerSelectAccount = (account: ACCOUNT) => {
    closeModal();
    setAccountDetail(undefined);
    setValue("customer", account?.ACCOUNTNO || "");
    setValue("accountNo", account?.ACCOUNTNO || "");
    clearErrors("customer");
    clearErrors("accountNo");
    setIsBene(false);
    handlerCheckAccountCredit(account.ACCOUNTNO);
  };

  const handlerSelectBene = (item: PROFILE_BENE) => {
    closeModal();
    setAccountDetail(undefined);
    let customer = item?.ACCOUNT_NUMBER || "";
    setValue("accountNo", customer);
    clearErrors("accountNo");
    if (item.PHONE?.length) {
      customer = item?.PHONE || "";
    }
    setValue("customer", customer);
    clearErrors("customer");
    setAccountBene(item);
    setIsBene(true);
    handlerCheckAccountCredit(customer);
  };

  const onChangeCustomer = () => {
    if (customerType !== TypeCustomer.OTHER) setCustomerType(TypeCustomer.OTHER);
    if (!searchCustomer) setSearchCustomer(true);
    if (accountDetail) setAccountDetail(undefined);
  };

  // Lấy thông tin tk nhận
  const onBlurAccountCredit = () => {
    const customer = getValues("customer");
    if (
      customer.length &&
      ((accountBene?.ACCOUNT_NUMBER?.length && customer === accountBene?.ACCOUNT_NUMBER) ||
        (accountBene?.PHONE?.length && customer === accountBene?.PHONE))
    ) {
      setIsBene(true);
    } else {
      setIsBene(false);
    }
    if (searchCustomer) {
      handlerCheckAccountCredit(customer);
    }
  };

  const onPressCreate = () => {
    onSubmit();
    handleSubmit(handleRequestCreate).call(() => {});
  };

  const onPressCancel = () => {
    onCancel();
  };

  const onPressFeeDetail = async () => {
    onSubmit();
    const accountTrigger = await trigger("accountNo");
    const amountTrigger = await trigger("amount");
    if (accountTrigger && amountTrigger) {
      const dataUpdate: DataTransferCreate = {
        ACCOUNT_CREDIT: {
          ACCOUNTNO: getValues("accountNo"),
          ACCOUNTTYPE: "10",
          ACCOUNTNAME: getValues("accountName"),
        },
        AMOUNT: getValues("amount"),
        DESCRIPTION: getValues("description"),
        FEE_TYPE: getValues("feeType") === "0" ? Constant.TRANSFER.TRANSFER_FEE_TYPE.O : Constant.TRANSFER.TRANSFER_FEE_TYPE.I,
      };
      if (dataFee) setDataFee(undefined);
      requestMakeFee(dataUpdate);
    }
  };

  const onPressBeneAndAccount = () => {
    dispatch(resetListBene());
    showModal(
      <SelectAccountsBeneModal
        account={accountDebit}
        templateType={Constant.TEMPLATE_TYPE.INTERNAL_TRANSFER}
        onPressAcceptAccount={handlerSelectAccount}
        onPressAcceptBene={handlerSelectBene}
      />,
    );
  };

  // fix/replace nội dung chuyển tiền
  const onBlurDescription = () => {
    const value = getValues("description") || "";
    setValue("description", replaceEnterKey(replaceSpace(nonAccentVietnamese(value.trim()))));
    trigger("description");
  };

  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: { flex: 1 },
        fieldFromText: {
          color: colors.text,
          width: 200,
          fontWeight: "700",
          fontSize: 16,
        },
        buttonContainer: { width: 128, alignSelf: "center", backgroundColor: colors.notification },
        buttonSave: { width: 80, alignSelf: "center", backgroundColor: colors.primary, position: "absolute" },
        acceptButtonContainer: { width: 128, alignSelf: "center" },
        displayName: { display: accountDetail?.ACCOUNTNAME?.length ? "flex" : "none" },
      }),
    [colors, accountDetail?.ACCOUNTNAME],
  );

  const chooseAccountBene = (
    <TextInput.Icon name={() => <IconsYellowBanking name={"danhsachthuhuong"} size={28} />} size={28} onPress={onPressBeneAndAccount} />
  );

  const FieldForm = (
    <Block>
      <StackSpacing size="medium" />
      <Paragraph style={styles.fieldFromText}>{t("TRANFER_TAI_KHOAN_NHAN")}</Paragraph>
      <StackSpacing size="medium" />
    </Block>
  );

  const FormFee = (
    <>
      <InputSegment
        label={t("TRANFER_BEN_CHIU_PHI")}
        name="feeType"
        control={control}
        values={[t("TRANFER_BEN_CHUYEN"), t("TRANFER_BEN_NHAN")]}
      />
      <FormViewFee onClick={onPressFeeDetail} />
    </>
  );

  const FormInput = (
    <Block flex={1} bg={colors.backgroundForm} br={12} p={spacing.large}>
      <InputText
        rules={validateSchemeForm.customer}
        name="customer"
        control={control}
        inputProps={{ maxLength: 19, right: chooseAccountBene }}
        label={
          customerType === TypeCustomer.PHONE
            ? t("TRANFER_SO_DIEN_THOAI")
            : customerType === TypeCustomer.ACCOUNT_NO
            ? t("TRANFER_SO_TAI_KHOAN")
            : t("TRANFER_SO_DIEN_THOAI_SO_TAI_KHOAN")
          // t("TRANFER_SO_TAI_KHOAN")
        }
        onBlur={onBlurAccountCredit}
        onChangedText={onChangeCustomer}
        keyboardType={"numeric"}
      />
      {customerType === TypeCustomer.PHONE ? (
        <InputText
          rules={validateSchemeForm.accountNo}
          name="accountNo"
          control={control}
          label={t("TRANFER_SO_TAI_KHOAN")}
          keyboardType={"numeric"}
        />
      ) : (
        <></>
      )}
      <InputText
        rules={validateSchemeForm.accountName}
        name="accountName"
        control={control}
        inputProps={{ editable: false }}
        style={styles.displayName}
        label={t("TRANFER_TEN_TAI_KHOAN")}
      />
      <InputTextCurrency
        name="amount"
        keyboardType={"numeric"}
        control={control}
        rules={validateSchemeForm.amount}
        label={t("TRANFER_SO_TIEN")}
        rightCurrency={t("COMMON_VND")}
      />
      <InputTextMultiline
        name="description"
        control={control}
        rules={validateSchemeForm.description}
        label={t("TRANFER_NOI_DUNG_CHUYEN_TIEN")}
        note={t("COMMON_NOTE_DESCRIPTION")}
        onBlur={onBlurDescription}
        inputProps={{ multiline: true, maxLength: 100 }}
        rightAffix={true}
      />
      {FormFee}
    </Block>
  );

  const ButtonForm = (
    <Block row middle pv={spacing.huge}>
      <Block w={isDesktop ? 200 : 0} />
      <Button onPress={onPressCancel} mode="contained" style={styles.buttonContainer}>
        {t("COMMON_HUY")}
      </Button>
      <QueueSpacing size="large" />
      <Button {...testProps("nextBtn")} mode="contained" style={styles.acceptButtonContainer} onPress={onPressCreate}>
        {t("COMMON_TIEP_TUC")}
      </Button>
    </Block>
  );

  // // map nội dung mặc định
  useEffect(() => {
    if (accountDebit.ACCOUNTNAME?.length && !getValues("description").length) {
      const content = accountDebit.ACCOUNTNAME.toUpperCase() + " " + t("TRANFER_DESCRIPTION_CHUYEN_TIEN").toLowerCase();
      setValue("description", replaceEnterKey(replaceSpace(nonAccentVietnamese(content))));
      clearErrors("description");
    }
  }, [accountDebit, clearErrors, getValues, setValue, t]);

  // account credit detail
  useEffect(() => {
    setValue("accountNo", accountDetail?.ACCOUNTNO || "");
    setValue("accountName", accountDetail?.ACCOUNTNAME || "");
  }, [accountDetail, setValue]);

  useEffect(() => {
    if (accountCredit && accountCredit.ACCOUNTNAME?.length && customerType !== TypeCustomer.PHONE) {
      accountCredit.ACCOUNTNO = getValues("customer");
    }
    setAccountDetail(accountCredit);
  }, [accountCredit, customerType, getValues, setValue]);

  useEffect(() => {
    if (accountCreditByPhone) {
      dispatch(
        fetchTransferLocalAccountDetailSuccess({
          ACCOUNTNO: accountCreditByPhone.accountno,
          ACCOUNTTYPE: "10",
          ACCOUNTNAME: accountCreditByPhone.accountname,
        }),
      );
    }
  }, [accountCreditByPhone, dispatch, setValue]);

  useEffect(() => {
    // lấy hạn mức gd ngày/ số dư khả dụng
    const { maxAmount, errorMaxDebit } = amountConfig(accountDebit, limitRemainStore, getValues("accountNo"));
    setValidateAmount({ maxAmount, errorMaxDebit });
  }, [limitRemainStore, accountDebit, getValues]);

  useEffect(() => {
    if (validateAmount?.errorMaxDebit?.length && getValues("amount").length) trigger("amount");
  }, [getValues, trigger, validateAmount]);

  useEffect(() => {
    if (!accountBene?.ACCOUNT_NUMBER?.length && transferLocalStore.dataCreate?.BENE?.ACCOUNT_NUMBER) {
      setAccountBene(transferLocalStore.dataCreate?.BENE);
      if (transferLocalStore.dataCreate?.BENE?.ACCOUNT_NUMBER === getValues("accountNo")) setIsBene(true);
    }
  }, [accountBene, getValues, transferLocalStore]);

  useEffect(() => {
    if (responseMakeFee?.TRANSFER) {
      setDataFee(responseMakeFee.TRANSFER);
    }
  }, [responseMakeFee]);

  // // show Phí
  useEffect(() => {
    if (dataFee) {
      showModal(
        <FeeDetailModalNew
          data={dataFee}
          feeType={getValues("feeType") === "0" ? Constant.TRANSFER.TRANSFER_FEE_TYPE.O : Constant.TRANSFER.TRANSFER_FEE_TYPE.I}
        />,
      );
      setDataFee(undefined);
    }
  }, [accountDebit, getValues, dataFee, showModal]);

  return (
    <>
      <Block row={isDesktop ? true : false} pt={spacing.medium}>
        {FieldForm}
        {FormInput}
      </Block>
      {ButtonForm}
    </>
  );
};

export default FormTransLocal;
